import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["recordForm", "recordInput", "recordLabel", "uploadForm", "uploadInput"]
  static values = {
    recordLabel: String,
    stopLabel: String,
    savingLabel: String,
    unsupportedMessage: String,
    emptyMessage: String
  }

  connect() {
    this.mediaRecorder = null
    this.chunks = []
    this.stream = null
    this.recording = false
  }

  disconnect() {
    this.stopTracks()
  }

  openPicker() {
    this.uploadInputTarget.click()
  }

  submitUpload() {
    this.uploadFormTarget.requestSubmit()
  }

  async toggleRecording() {
    if (this.recording) {
      this.recording = false
      if (this.mediaRecorder && typeof this.mediaRecorder.requestData === "function") {
        this.mediaRecorder.requestData()
      }
      this.mediaRecorder.stop()
      return
    }

    if (!this.recordingSupported()) {
      this.handleRecordingUnavailable()
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = this.preferredRecordingMimeType()
      this.mediaRecorder = mimeType ? new MediaRecorder(this.stream, { mimeType }) : new MediaRecorder(this.stream)
    } catch (_error) {
      this.handleRecordingUnavailable()
      return
    }

    this.chunks = []

    this.mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) this.chunks.push(event.data)
    })

    this.mediaRecorder.addEventListener("stop", () => {
      const blobType = this.mediaRecorder.mimeType || this.preferredRecordingMimeType() || "audio/webm"
      const blob = new Blob(this.chunks, { type: blobType })
      if (blob.size === 0) {
        this.handleEmptyRecording()
        return
      }
      const file = new File([blob], this.recordedFilename(blob.type), { type: blob.type })
      if (this.hasRecordLabelTarget) {
        this.recordLabelTarget.textContent = this.savingLabelValue || this.stopLabelValue
      }
      void this.submitRecordedFile(file)
    })

    this.mediaRecorder.start(250)
    this.recording = true
    this.recordLabelTarget.textContent = this.stopLabelValue
  }

  recordingSupported() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return false
    }

    return typeof MediaRecorder !== "undefined"
  }

  preferredRecordingMimeType() {
    if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
      return null
    }

    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4"
    ]

    return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || null
  }

  recordedFilename(mimeType) {
    if (mimeType.startsWith("audio/mp4")) return "voice-note.m4a"
    if (mimeType.startsWith("audio/ogg")) return "voice-note.ogg"
    if (mimeType.startsWith("audio/webm")) return "voice-note.webm"

    return "voice-note.audio"
  }

  handleRecordingUnavailable() {
    this.stopTracks()
    this.recording = false
    if (this.hasRecordLabelTarget) {
      this.recordLabelTarget.textContent = this.recordLabelValue
    }
    window.alert(this.unsupportedMessageValue)
  }

  handleEmptyRecording() {
    this.stopTracks()
    this.recording = false
    if (this.hasRecordLabelTarget) {
      this.recordLabelTarget.textContent = this.recordLabelValue
    }
    window.alert(this.emptyMessageValue || this.unsupportedMessageValue)
  }

  stopTracks() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
  }

  async submitRecordedFile(file) {
    try {
      if (typeof DataTransfer !== "undefined") {
        const transfer = new DataTransfer()
        transfer.items.add(file)
        this.recordInputTarget.files = transfer.files
        this.recordFormTarget.requestSubmit()
        return
      }

      const formData = new FormData(this.recordFormTarget)
      formData.set("audio_file", file, file.name)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.recordFormTarget.action, {
        method: (this.recordFormTarget.method || "post").toUpperCase(),
        body: formData,
        credentials: "same-origin",
        headers: {
          Accept: "text/html, application/xhtml+xml",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
        }
      })

      if (response.redirected) {
        window.location.assign(response.url)
        return
      }

      window.location.reload()
    } finally {
      this.stopTracks()
      this.recording = false
      if (this.hasRecordLabelTarget) {
        this.recordLabelTarget.textContent = this.recordLabelValue
      }
    }
  }
}
